import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { getRelatedMatches } from "../../lib/leaderboardApi";
import DiscoverySelect from "./DiscoverySelect";
import "./MatchDiscovery.css";
import "./RelatedMatchSelector.css";

const sections = new Set(["overview", "registrations", "registration-settings", "waitlist", "squads", "setup", "information", "settings", "updates", "results-review"]);

export function relatedMatchPath(family, matchId, surface = "detail", section = "overview") {
  const path = `/matches/${family}/${encodeURIComponent(matchId)}`;
  if (surface === "director") return `/matches/manage/${family}/${encodeURIComponent(matchId)}${sections.has(section) && section !== "overview" ? `/${section}` : ""}`;
  if (surface === "display") return `${path}/display`;
  if (surface === "results" && family === "bullseye") return `${path}/results`;
  return path;
}

export default function RelatedMatchSelector({ match, family, surface = "detail", section, refreshKey = 0 }) {
  const matchId = match?.matchId;
  const navigate = useNavigate(), location = useLocation();
  const [state, setState] = useState({}), [revision, setRevision] = useState(0);
  const key = `${family}:${matchId}`;
  useEffect(() => {
    if (!matchId || !["arc", "bullseye"].includes(family)) return undefined;
    let active = true;
    setState({ key, loading: true });
    (async () => {
      try {
        const group = await getRelatedMatches(matchId, family);
        if (active) setState({ key, group });
      } catch (error) {
        if (active) setState({ key, error: true });
      }
    })();
    return () => { active = false; };
  }, [key, family, matchId, revision, refreshKey]);

  if (state.key !== key || state.loading) return null;
  if (state.error) return <div className="related-match-error" role="status"><span>Related matches unavailable</span><button type="button" onClick={() => setRevision(value => value + 1)} aria-label="Retry related matches" title="Retry related matches"><RefreshCw size={16} /></button></div>;
  const group = state.group;
  if (!group?.mainMatch || !group.sideMatches?.length) return null;
  const options = [group.mainMatch, ...group.sideMatches].map(item => ({ value: item.matchId,
    label: `${item.matchId === group.mainMatch.matchId ? "Main Match" : `Side Match ${item.sideMatchOrder}`} - ${item.matchName || "Untitled match"}` }));
  if (!options.some(item => item.value === matchId)) return null;
  function change(id) {
    if (id === matchId || !options.some(item => item.value === id)) return;
    // Stage, course, competitor and print parameters belong to the previous match.
    navigate(relatedMatchPath(family, id, surface, section), { state: {
      discoveryPath: location.state?.discoveryPath, displayReturn: location.state?.displayReturn,
    } });
  }
  if (surface === "display") return <label className="booth-top-control related-match-display"><span>Match</span><select aria-label="Match" value={matchId} onChange={event => change(event.target.value)}>{options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>;
  return <nav className="related-match-selector" aria-label="Related matches"><span className="related-match-label">Match</span><DiscoverySelect label="Match" value={matchId} options={options} onChange={change} searchable={options.length > 6} multilineOptions /></nav>;
}
